/**
 * What the subaccount fund / defund MAX buttons resolve to.
 *
 * Both read a collateral balance straight off the spot engine, in base units.
 *
 * The distinction that matters here is between "zero" and "not read". Today
 * `useSubaccounts.refreshBalances` catches a failed `getBalance` and stores the
 * string `"0"`, so a chain hiccup is indistinguishable from an empty account and
 * MAX confidently offers nothing. An unread balance must reach these helpers as
 * `undefined`, and comes back as `balance_unknown`.
 */
import { Dec, dec } from "../decimal";
import { MaxResult, maxOk, maxUnavailable } from "./types";

export interface SubaccountFundMaxInput {
  /** The master's free engine collateral, in base units; absent = unread. */
  masterFreeUnits?: bigint;
  usdcDecimals: number;
}

export interface SubaccountDefundMaxInput {
  /** The subaccount's free engine collateral, in base units; absent = unread. */
  subFreeUnits?: bigint;
  usdcDecimals: number;
  /**
   * The subaccount's margin-aware withdrawable, in USD. `null` is the backend
   * saying it cannot value the subaccount; `undefined` is "no cap supplied".
   * USDC is the collateral, so USD and token terms are the same figure.
   */
  subWithdrawableUsd?: Dec | null;
}

const freeBalance = (units: bigint, decimals: number): Dec => {
  const amount = dec.round(dec.fromUnits(units, decimals), decimals, "floor");
  return dec.isNeg(amount) ? "0" : amount;
};

export const subaccountFundMax = (input: SubaccountFundMaxInput): MaxResult => {
  if (input.masterFreeUnits === undefined) {
    return maxUnavailable("balance_unknown");
  }
  return maxOk(
    freeBalance(input.masterFreeUnits, input.usdcDecimals),
    "balance",
    "USDC"
  );
};

export const subaccountDefundMax = (
  input: SubaccountDefundMaxInput
): MaxResult => {
  if (input.subWithdrawableUsd === null) {
    return maxUnavailable("withdrawable_unknown");
  }
  if (input.subFreeUnits === undefined) {
    return maxUnavailable("balance_unknown");
  }

  const balance = freeBalance(input.subFreeUnits, input.usdcDecimals);
  if (input.subWithdrawableUsd === undefined) {
    return maxOk(balance, "balance", "USDC");
  }

  const withdrawable = dec.parse(input.subWithdrawableUsd);
  if (withdrawable === null) return maxUnavailable("withdrawable_unknown");

  const cap = dec.isNeg(withdrawable)
    ? "0"
    : dec.round(withdrawable, input.usdcDecimals, "floor");

  return dec.lt(cap, balance)
    ? maxOk(cap, "withdrawable", "USDC")
    : maxOk(balance, "balance", "USDC");
};
